import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { blogPosts } from "../data/mock";
import { Calendar, User, ArrowUpRight, Clock } from "lucide-react";

const categories = ["All", ...Array.from(new Set(blogPosts.map((p) => p.category)))];

const Blog = () => {
  const [active, setActive] = useState("All");
  const [search, setSearch] = useState("");
  const filtered = blogPosts.filter((p) => {
    const cat = active === "All" || p.category === active;
    const q = search.toLowerCase();
    const text = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
    return cat && text;
  });
  const featured = blogPosts[0];

  return (
    <PageLayout>
      <PageHero
        badge="Insights & Guides"
        title="Blog & Resources"
        subtitle="Field-tested guides, case studies, and reference material from our shop floor and engineering team."
        breadcrumbs={[{ label: "Blog" }]}
      />

      {/* Featured */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <Link
            to={`/blog/${featured.slug}`}
            className="group grid lg:grid-cols-2 gap-8 rounded-3xl overflow-hidden bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500"
          >
            <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <span className="absolute top-5 left-5 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "linear-gradient(135deg, #10b981, #047857)" }}>
                Featured
              </span>
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider">{featured.category}</span>
                <span className="flex items-center gap-1"><Calendar size={13} /> {featured.date}</span>
                <span className="flex items-center gap-1"><Clock size={13} /> {featured.readTime}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight group-hover:text-emerald-700 transition-colors" style={{ color: "#0a2e1f" }}>
                {featured.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 group-hover:gap-3 transition-all">
                Read article <ArrowUpRight size={16} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6 pt-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-10">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
                    active === c
                      ? "text-white"
                      : "bg-white text-slate-600 hover:text-emerald-700 border border-slate-200"
                  }`}
                  style={
                    active === c
                      ? { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }
                      : {}
                  }
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="px-5 py-2.5 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 bg-white shadow-sm md:w-72"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">No articles found.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group block bg-white rounded-3xl overflow-hidden border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                    <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm" style={{ background: "rgba(16,185,129,0.9)" }}>
                      {p.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {p.readTime}</span>
                    </div>
                    <h3 className="text-base font-bold mb-2 leading-snug group-hover:text-emerald-700 transition-colors" style={{ color: "#0a2e1f" }}>
                      {p.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2 mb-4">{p.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-slate-500">
                        <User size={12} /> {p.author}
                      </span>
                      <ArrowUpRight size={16} className="text-emerald-600 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default Blog;
